import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import {
  fetchAutopilotConnectorDlqUploads,
  fetchAutopilotConnectorUploads,
  fetchAutopilotDiagnostics,
  fetchAutopilotBudgetRecommendations,
  updateAutopilotBudgetRecommendation,
  fetchAutopilotSegmentationSnapshot,
  triggerAutopilotSegmentationRun,
  fetchAutopilotExperiments,
  updateAutopilotExperimentVariant,
  fetchAutopilotRollups,
  downloadAutopilotRollupsCsv,
  fetchAutopilotHeuristicsConfig,
  updateAutopilotHeuristicsConfig,
  fetchAutopilotOptimizationAnalytics,
  fetchAutopilotOfferIntelligence,
  fetchAutopilotFranchiseAnalytics,
  downloadAutopilotFranchiseAnalyticsCsv,
  type AutopilotConnectorDiagnostic,
  type AutopilotConnectorUpload,
  type AutopilotBudgetRecommendationWithEvents,
  type AutopilotBudgetRecommendationStatus,
  type AutopilotSegmentationAsyncResponse,
  type AutopilotSegmentationSnapshot,
  type AutopilotSegmentationSyncResponse,
  type AutopilotExperimentState,
  type AutopilotRollup,
  type AutopilotRollupExportLog,
  type AutopilotHeuristicsConfig,
  type AutopilotHeuristicRuleSummary,
  type AutopilotOptimizationAnalytics,
  type AutopilotOptimizationRun,
  type AutopilotOfferIntelligence,
  type AutopilotFranchiseAnalyticsResponse,
} from '@/lib/api/autopilot';
import { useTenantSlug } from '@/stores/tenantStore';

type ConnectorType = AutopilotConnectorDiagnostic['connector'];

type ConnectorKey = {
  connector?: ConnectorType;
  environment?: 'sandbox' | 'production';
  limit?: number;
};

export const useAutopilotDiagnostics = (): UseQueryResult<AutopilotConnectorDiagnostic[]> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'diagnostics', tenantSlug],
    enabled: Boolean(tenantSlug),
    staleTime: 30_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve([]);
      }
      return fetchAutopilotDiagnostics({
        tenantSlug,
        signal,
        environment: 'sandbox',
        limit: 5,
      });
    },
  });
};

export const useAutopilotConnectorUploads = (
  key: ConnectorKey,
): UseQueryResult<AutopilotConnectorUpload[]> => {
  const tenantSlug = useTenantSlug();
  const { connector, environment = 'sandbox', limit = 10 } = key;
  return useQuery({
    queryKey: ['autopilot', 'uploads', tenantSlug, connector ?? 'all', environment, limit],
    enabled: Boolean(tenantSlug),
    staleTime: 20_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve([]);
      }
      return fetchAutopilotConnectorUploads({
        tenantSlug,
        signal,
        connector,
        environment,
        limit,
      });
    },
  });
};

export const useAutopilotConnectorDlqUploads = (
  key: Omit<ConnectorKey, 'limit'> & { limit?: number },
): UseQueryResult<AutopilotConnectorUpload[]> => {
  const tenantSlug = useTenantSlug();
  const { connector, environment = 'sandbox', limit = 10 } = key;
  return useQuery({
    queryKey: ['autopilot', 'uploads', 'dlq', tenantSlug, connector ?? 'all', environment, limit],
    enabled: Boolean(tenantSlug),
    staleTime: 15_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve([]);
      }
      return fetchAutopilotConnectorDlqUploads({
        tenantSlug,
        signal,
        connector,
        environment,
        limit,
      });
    },
  });
};

export const useAutopilotSegmentationSnapshot = (): UseQueryResult<AutopilotSegmentationSnapshot> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'segmentation', 'snapshot', tenantSlug],
    enabled: Boolean(tenantSlug),
    staleTime: 60_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve({ ok: true, run: null, segments: [], suppressionAudits: [] });
      }
      return fetchAutopilotSegmentationSnapshot({
        tenantSlug,
        signal,
      });
    },
  });
};

type SegmentationRunParams = {
  mode?: 'async' | 'sync';
};

export const useTriggerAutopilotSegmentation = (): UseMutationResult<
  AutopilotSegmentationAsyncResponse | AutopilotSegmentationSyncResponse,
  unknown,
  SegmentationRunParams | undefined
> => {
  const tenantSlug = useTenantSlug();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params?: SegmentationRunParams) => {
      if (!tenantSlug) {
        throw new Error('Tenant slug required to trigger segmentation run.');
      }
      return triggerAutopilotSegmentationRun({
        tenantSlug,
        mode: params?.mode ?? 'async',
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['autopilot', 'segmentation', 'snapshot', tenantSlug] });
    },
  });
};

export const useAutopilotBudgetRecommendations = (): UseQueryResult<
  AutopilotBudgetRecommendationWithEvents[]
> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'budgets', 'recommendations', tenantSlug],
    enabled: Boolean(tenantSlug),
    staleTime: 20_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve([]);
      }
      return fetchAutopilotBudgetRecommendations({
        tenantSlug,
        signal,
      });
    },
  });
};

export const useUpdateAutopilotBudgetRecommendation = (): UseMutationResult<
  AutopilotBudgetRecommendationWithEvents,
  unknown,
  {
    recommendationId: string;
    status: AutopilotBudgetRecommendationStatus;
    note?: string | null;
    scheduledFor?: string | null;
    targetBudgetCents?: number | null;
    actorName?: string | null;
  }
> => {
  const tenantSlug = useTenantSlug();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      if (!tenantSlug) {
        throw new Error('Tenant slug required to update budget recommendation.');
      }
      return updateAutopilotBudgetRecommendation({
        tenantSlug,
        recommendationId: input.recommendationId,
        body: {
          status: input.status,
          note: input.note ?? null,
          scheduledFor: input.scheduledFor ?? null,
          targetBudgetCents: input.targetBudgetCents ?? null,
          actorName: input.actorName ?? null,
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['autopilot', 'budgets', 'recommendations', tenantSlug] });
    },
  });
};

export const useAutopilotExperiments = (): UseQueryResult<AutopilotExperimentState[]> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'experiments', tenantSlug],
    enabled: Boolean(tenantSlug),
    staleTime: 30_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve([]);
      }
      return fetchAutopilotExperiments({ tenantSlug, signal });
    },
  });
};

export const useUpdateAutopilotExperiment = (): UseMutationResult<
  AutopilotExperimentState,
  unknown,
  { experimentKey: string; variant: string; notes?: string | null }
> => {
  const tenantSlug = useTenantSlug();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      if (!tenantSlug) {
        throw new Error('Tenant slug required to update Autopilot experiment.');
      }
      return updateAutopilotExperimentVariant({
        tenantSlug,
        experimentKey: input.experimentKey,
        body: {
          variant: input.variant,
          notes: input.notes ?? null,
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['autopilot', 'experiments', tenantSlug] });
    },
  });
};

export const useAutopilotRollups = ({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
} = {}): UseQueryResult<{
  rollups: AutopilotRollup[];
  exports: AutopilotRollupExportLog[];
}> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'rollups', tenantSlug, startDate ?? null, endDate ?? null],
    enabled: Boolean(tenantSlug),
    staleTime: 30_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve({ rollups: [], exports: [] });
      }
      return fetchAutopilotRollups({ tenantSlug, signal, startDate, endDate });
    },
  });
};

export const useDownloadAutopilotRollupsCsv = (): UseMutationResult<Blob, unknown, { startDate?: string; endDate?: string }> => {
  const tenantSlug = useTenantSlug();
  return useMutation({
    mutationFn: (params) => {
      if (!tenantSlug) {
        throw new Error('Tenant slug required to download rollups.');
      }
      return downloadAutopilotRollupsCsv({ tenantSlug, startDate: params?.startDate, endDate: params?.endDate });
    },
  });
};

export const useAutopilotFranchiseAnalytics = ({
  startDate,
  endDate,
  connector,
}: {
  startDate?: string;
  endDate?: string;
  connector?: string;
} = {}): UseQueryResult<AutopilotFranchiseAnalyticsResponse> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'franchise', 'analytics', tenantSlug, startDate ?? null, endDate ?? null, connector ?? null],
    enabled: Boolean(tenantSlug),
    staleTime: 30_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        const nowIso = new Date().toISOString();
        return Promise.resolve({
          ok: true as const,
          rollups: [],
          window: {
            startDate: startDate ?? nowIso,
            endDate: endDate ?? nowIso,
          },
        });
      }
      return fetchAutopilotFranchiseAnalytics({ tenantSlug, signal, startDate, endDate, connector });
    },
  });
};

export const useDownloadAutopilotFranchiseCsv = (): UseMutationResult<
  Blob,
  unknown,
  { startDate?: string; endDate?: string; connector?: string }
> => {
  const tenantSlug = useTenantSlug();
  return useMutation({
    mutationFn: (params) => {
      if (!tenantSlug) {
        throw new Error('Tenant slug required to download franchise analytics.');
      }
      return downloadAutopilotFranchiseAnalyticsCsv({
        tenantSlug,
        startDate: params?.startDate,
        endDate: params?.endDate,
        connector: params?.connector,
      });
    },
  });
};

export const useAutopilotHeuristicsConfig = (): UseQueryResult<{
  config: AutopilotHeuristicsConfig | null;
  rules: AutopilotHeuristicRuleSummary[];
}> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'heuristics', 'config', tenantSlug],
    enabled: Boolean(tenantSlug),
    staleTime: 30_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve({ config: null, rules: [] });
      }
      return fetchAutopilotHeuristicsConfig({ tenantSlug, signal }).then((payload) => ({
        config: payload.config,
        rules: payload.rules,
      }));
    },
  });
};

export const useUpdateAutopilotHeuristicsConfig = (): UseMutationResult<
  {
    config: AutopilotHeuristicsConfig;
    rules: AutopilotHeuristicRuleSummary[];
  },
  unknown,
  {
    killSwitchEnabled?: boolean;
    maxDailyBudgetDeltaPercent?: number;
    maxActionsPerRun?: number;
    defaultBundle?: string | null;
  }
> => {
  const tenantSlug = useTenantSlug();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      if (!tenantSlug) {
        throw new Error('Tenant slug required to update heuristics config.');
      }
      const response = await updateAutopilotHeuristicsConfig({ tenantSlug, payload });
      return { config: response.config, rules: response.rules };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['autopilot', 'heuristics', 'config', tenantSlug] });
      void queryClient.invalidateQueries({ queryKey: ['autopilot', 'optimization', 'analytics', tenantSlug] });
    },
  });
};

export const useAutopilotOptimizationAnalytics = (): UseQueryResult<{
  metrics: AutopilotOptimizationAnalytics;
  recentRuns: AutopilotOptimizationRun[];
}> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'optimization', 'analytics', tenantSlug],
    enabled: Boolean(tenantSlug),
    staleTime: 30_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve({
          metrics: {
            incrementalRoas: 1,
            costPerNewDiner: 0,
            averageLagMinutes: 0,
            offerStates: { enabled: 0, disabled: 0, snoozed: 0 },
            lastUpdatedAt: null,
          },
          recentRuns: [],
        });
      }
      return fetchAutopilotOptimizationAnalytics({ tenantSlug, signal });
    },
  });
};

export const useAutopilotOfferIntelligence = (): UseQueryResult<AutopilotOfferIntelligence> => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'offers', 'intelligence', tenantSlug],
    enabled: Boolean(tenantSlug),
    staleTime: 30_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve({
          ok: true,
          offers: [],
          weather: {
            latestSeverity: null,
            temperatureCelsius: null,
            precipitationProbability: null,
            observedAt: null,
          },
          staffing: {
            coverageRatio: null,
            overtimeHours: null,
            callouts: null,
            observedAt: null,
          },
        } satisfies AutopilotOfferIntelligence);
      }
      return fetchAutopilotOfferIntelligence({ tenantSlug, signal });
    },
  });
};
